<script lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import { PropType, ref } from 'vue';

import ChatPanelContextBadge from '@/V2/chat/components/ChatPanelContextBadge.vue';
import { mapGetters } from 'vuex';
import { ChatContext, ChatContextType } from '@/types/chat';

let userInput = ref('');

export default {
  name: 'ChatPromptBox',
  components: { iButton, ChatPanelContextBadge },
  props: {
    conversationUuid: {
      type: String,
      required: true,
    },
    contexts: {
      type: Array as PropType<ChatContext[]>,
      required: true,
    },
  },
  setup() {
    return {
      userInput,
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters(['getIsGeneratingForConversationUuid']),
    isGenerating() {
      return this.getIsGeneratingForConversationUuid(this.conversationUuid);
    },
  },
  methods: {
    onStopGeneration() {
      this.$emit('stop-generation');
    },
    async sendMessage() {
      if (!this.userInput.trim()) {
        return;
      }

      // If user tries to send a message while generating, stop the generation
      // and send the message
      if (this.getChatIsGenerating) {
        this.onStopGeneration();
      }

      const userMessageContent = this.userInput.trim();
      this.$emit('submit-message', userMessageContent);
      this.userInput = ''; // Clear input immediately
    },
    getKeyForContext(context: ChatContext) {
      switch (context.type) {
        case ChatContextType.Customer:
          return context.customerUuid;
        case ChatContextType.Document:
          return context.documentId;
        case ChatContextType.Global:
          return 'global';
      }
    },
  },
};
</script>

<template>
  <div class="chat-prompt-box d-flex flex-column i-radius-2xl i-p-2 i-gap-2">
    <div v-if="contexts.length > 0" class="header d-flex i-gap-2 i-p-2">
      <chat-panel-context-badge
        v-for="context in contexts"
        :key="getKeyForContext(context)"
        :context="context"
      ></chat-panel-context-badge>
    </div>
    <div class="input-area d-flex align-items-center">
      <textarea
        v-model="userInput"
        data-testid="chat-panel-prompt-box-textarea"
        class="i-py-6 i-pl-6 i-pr-12 i-text-subheading-2 i-radius-2xl"
        rows="2"
        placeholder="Ask a question"
        @keydown.enter.exact.prevent="sendMessage"
      ></textarea>

      <i-button
        v-if="!isGenerating"
        :button-style="iButtonStyles.SOLID_DARK"
        :size="iButtonSizes.SMALL"
        :type="iButtonTypes.NEUTRAL"
        :disabled="!userInput.trim()"
        :left-icon="'medium-arrows-up'"
        class="float-button"
        @button-click="sendMessage"
      >
      </i-button>
      <i-button
        v-else
        :button-style="iButtonStyles.SOLID_DARK"
        :size="iButtonSizes.SMALL"
        :type="iButtonTypes.NEUTRAL"
        :left-icon="'medium_playback_stop'"
        class="float-button"
        @button-click="onStopGeneration"
      >
      </i-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-prompt-box {
  position: relative;
  background: var(--surface-neutral);

  .float-button {
    position: absolute;
    right: 16px;
    bottom: 16px;
  }

  textarea {
    resize: none;
    background-color: var(--base);
    color: var(--text-neutral-bold);
    border: none;
    border-radius: 12px;
    width: 100%;
    min-height: 42px;
    outline: none;
  }
}
</style>
